import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaguesApi } from '../../api/leagues';
import GroupTable, { StandingEntry } from '../../components/GroupTable';
import KoBracket from '../../components/KoBracket';
import { Match } from '../../api/matches';
import { useTranslation } from 'react-i18next';

export default function AdminLeagueDraw() {
  const { id } = useParams<{ id: string }>();
  const leagueId = parseInt(id!);
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { t } = useTranslation();

  const { data: league } = useQuery({
    queryKey: ['league', leagueId],
    queryFn: () => leaguesApi.get(leagueId).then((r) => r.data),
  });

  const isRoundRobin = league?.league_format === 'round_robin';

  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['league-groups', leagueId],
    queryFn: () => leaguesApi.getGroups(leagueId).then((r) => r.data),
    enabled: !!league && !isRoundRobin && ['active', 'knockout', 'finished'].includes(league?.status ?? ''),
  });

  const { data: matches = [], isLoading: matchesLoading } = useQuery({
    queryKey: ['league-matches', leagueId],
    queryFn: () => leaguesApi.getMatches(leagueId).then((r) => r.data as Match[]),
    enabled: !!league && ['active', 'knockout', 'finished'].includes(league?.status ?? ''),
  });

  const { data: standings = [] } = useQuery({
    queryKey: ['league-standings', leagueId],
    queryFn: () => leaguesApi.getStandings(leagueId).then((r) => r.data as StandingEntry[]),
    enabled: isRoundRobin && !!league && ['active', 'finished'].includes(league?.status ?? ''),
  });

  const activateMutation = useMutation({
    mutationFn: () => leaguesApi.activate(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['league-groups', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['league-matches', leagueId] });
      setSuccess(t('admin.msgActivated'));
      setError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('admin.errorActivate'));
    },
  });

  const startKoMutation = useMutation({
    mutationFn: () => leaguesApi.startKnockout(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      queryClient.invalidateQueries({ queryKey: ['league-matches', leagueId] });
      setSuccess(t('admin.msgKOStarted'));
      setError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  const finishMutation = useMutation({
    mutationFn: () => leaguesApi.finish(leagueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['league', leagueId] });
      setSuccess(t('admin.msgFinished'));
      setError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('admin.error'));
    },
  });

  if (!league) return <div className="text-center py-16">{t('admin.loading')}</div>;

  const koMatches = (matches as Match[]).filter((m) => m.phase !== 'group');

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/admin/leagues/${leagueId}`} className="text-primary-600 hover:text-primary-700 text-sm">
          {t('admin.backToManage')}
        </Link>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">
          {isRoundRobin ? t('admin.scheduleTitle') : t('admin.drawTitle')} – {league.name}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm mb-4">
          {success}
        </div>
      )}

      {/* Control panel */}
      <div className="card mb-6">
        <h2 className="font-semibold text-gray-900 mb-4">{t('admin.actions')}</h2>
        <div className="flex flex-wrap gap-3">
          {['open', 'closed', 'draft'].includes(league.status) && (
            <button
              onClick={() => {
                const msg = isRoundRobin
                  ? t('admin.activateConfirmRR')
                  : t('admin.activateConfirm');
                if (window.confirm(msg)) {
                  activateMutation.mutate();
                }
              }}
              disabled={activateMutation.isPending}
              className="btn btn-primary"
            >
              {activateMutation.isPending
                ? t('admin.activating')
                : isRoundRobin ? t('admin.activateAndSchedule') : t('admin.activateAndDraw')}
            </button>
          )}
          {!isRoundRobin && league.status === 'active' && (
            <button
              onClick={() => {
                if (window.confirm(t('admin.koConfirm'))) {
                  startKoMutation.mutate();
                }
              }}
              disabled={startKoMutation.isPending}
              className="btn btn-primary"
            >
              {startKoMutation.isPending ? t('admin.starting') : t('admin.startKO')}
            </button>
          )}
          {(isRoundRobin ? ['active'] : ['active', 'knockout']).includes(league.status) && (
            <button
              onClick={() => {
                if (window.confirm(t('admin.finishConfirm'))) {
                  finishMutation.mutate();
                }
              }}
              disabled={finishMutation.isPending}
              className="btn btn-secondary"
            >
              {t('admin.finish')}
            </button>
          )}
        </div>

        {league.status === 'active' && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-700"
              dangerouslySetInnerHTML={{
                __html: isRoundRobin
                  ? t('admin.seasonRunning')
                  : t('admin.groupPhaseRunning', { count: league.qualifiers_per_group })
              }}
            />
          </div>
        )}
      </div>

      {/* Round-Robin: Overall standings */}
      {isRoundRobin && (standings as StandingEntry[]).length > 0 && (
        <div className="mb-6">
          <GroupTable
            groupName={t('leagueDetail.overall')}
            standings={standings as StandingEntry[]}
            qualifiersPerGroup={0}
            setLabel={league.ranking_rule === 'matches' ? t('groupTable.matchesLabel') : t('groupTable.fixtureWinsLabel')}
          />
        </div>
      )}

      {/* Groups (non-round-robin) */}
      {!isRoundRobin && (
        groupsLoading ? (
          <div className="text-center py-10 text-gray-400">{t('admin.loading')}</div>
        ) : (groups as any[]).length > 0 ? (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.groupPhaseSection')}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {(groups as any[]).map((group) => (
                <div key={group.id} className="bg-primary-50 border border-primary-100 rounded-xl p-4">
                  <GroupTable
                    groupName={group.name}
                    standings={group.standings ?? []}
                    qualifiersPerGroup={league.qualifiers_per_group}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          ['active', 'knockout', 'finished'].includes(league.status) && (
            <div className="card text-center py-8 text-gray-400 mb-6">{t('admin.noGroupsYet')}</div>
          )
        )
      )}

      {/* KO Bracket */}
      {!isRoundRobin && koMatches.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.koBracket')}</h2>
          <KoBracket matches={koMatches} />
        </div>
      )}
    </div>
  );
}
